import Image from 'next/image';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { BsFillCheckCircleFill } from 'react-icons/bs';
import displayInYen from '../../../core/displayInYen';
import DatePicker from '../../common/DatePicker/DatePicker';
import Button from '../../common/Button';
import GuestCounter from '../../common/GuestCounter/GuestCounter';
import RoomCard from './RoomCard';
import HotelRoom from '../../../../types/RoomAvailability';
import getMealPlanCategoryText from '../../../core/getMealPlanCategoryText';
import {
  Affilation,
  Category,
  COMMON_DATE_FORMAT,
  Currency,
  DEFAULT_CHECK_IN_DATE,
  DEFAULT_CHECK_OUT_DATE,
  Locale,
  MiniSiteTabOptions,
} from '../../../core/constants';
import useAvailability from './useAvailability';
import analytics from '../../../core/analytics/analytics';
import ApplicationStatuses from '../../../../types/ApplicationStatuses';
import { reportMessage } from '../../../core/errorReporting/errorReporting';
import MealPlanAccordion from './MealPlanAccordion';
import { useSelector } from '../../../core/redux/store';
import getGuestCount from '../../../core/booking/getGuestCount';
import PlanDetailModal from '../../common/PlanDetailModal/PlanDetailModal';
import AuthenticationSection from '../../account/AuthenticationSection';
import MealPlanCard from './MealPlanCard';

type MealPlan = NonNullable<HotelRoom['mealPlans']>[0];
type Props = {
  isTLLMaintenanceStatus: ApplicationStatuses | undefined;
  hotelId: string;
  locale: string | undefined;
};

const Availability = ({ hotelId, locale, isTLLMaintenanceStatus }: Props) => {
  const router = useRouter();
  const { mealPlanConfigModal } = useSelector((state) => state.planConfig);
  const { t: tCommon } = useTranslation('common');
  const { t: tHotel } = useTranslation('hotel');
  const [roomId, setRoomId] = useState<number | undefined>();
  const { userAttributes } = useSelector((x) => x.account);
  const isLoggedIn = Boolean(userAttributes?.accountId);
  const planListingFeature = false;

  const {
    hotel,
    getByIdLoading,
    getByIdFailed,
    roomAndGuestCount,
    setRoomAndGuestCount,
    checkInAndOutDates,
    setCheckInAndOutDates,
    getLinkUrl,
    guestCountString,
    updateUrl,
  } = useAvailability(router?.query?.hotel as string, !!isTLLMaintenanceStatus, router?.locale);

  const discountPercentage =
    hotel?.hotelRooms[0]?.mealPlans?.[0]?.planRates?.[0]?.dailyTotal?.discountPercentage ?? 0;
  const memberDiscountPercentage =
    hotel?.hotelRooms[0]?.mealPlans?.[0]?.planRates?.[0]?.dailyTotal?.memberDiscountPercentage ?? 0;

  const guestCount = getGuestCount(guestCountString);

  useEffect(() => {
    if ((hotel?.hotelRooms ?? []).length > 0) {
      analytics.google.ecommerce.track.item.list(
        (hotel?.hotelRooms ?? []).reduce(
          (accumulator: Analytics.Google.Ecommerce.Tracking.Item[], current) => {
            current.mealPlans?.forEach((plan) => {
              accumulator.push({
                affiliation: Affilation.WEB,
                item_id: plan.id || '',
                item_name: plan?.name || '',
                item_list_id: current?.id ? current?.id.toString() : '',
                item_list_name: current?.name || '',
                currency: Currency.JPY,
                item_category: Category.MEAL_PLAN,
              });
            });
            return accumulator;
          },
          []
        )
      );
    }
  }, [hotel?.hotelRooms]);

  const events = {
    onClickSearch: () => {
      updateUrl();
      // if the page is too old withtout being refreshed and checkInDate is before today's date
      // reflesh the page with default checkIn/Out dates without hitting the API
      if (!checkInAndOutDates.checkInDate?.isSameOrAfter(moment(), 'days')) {
        router.replace({
          pathname: router.pathname,
          query: {
            ...router.query,
            checkInDate: DEFAULT_CHECK_IN_DATE,
            checkOutDate: DEFAULT_CHECK_OUT_DATE,
            tab: MiniSiteTabOptions.ROOMS,
          },
        });
        return;
      }

      if (checkInAndOutDates.checkInDate && checkInAndOutDates.checkOutDate) {
        analytics.facebook.track.view({
          content_type: 'hotel',
          checkin_date: checkInAndOutDates.checkInDate.format(COMMON_DATE_FORMAT),
          checkout_date: checkInAndOutDates.checkOutDate.format(COMMON_DATE_FORMAT),
          content_ids: hotel?.id || '',
          city: hotel?.address?.prefecture || undefined,
          region: hotel?.address?.region || undefined,
          country: locale === Locale.JAPANESE ? '日本' : 'Japan',
          num_rooms: roomAndGuestCount.roomCount,
          num_adults: guestCount?.adultCount,
          num_children: Object.entries(guestCount).reduce((accumulator, [key, value]) => {
            const result = accumulator + key !== 'adultCount' ? value : 0;
            return result;
          }, 0),
        });
      }
    },
  };

  const renderPlanCategories = (hotelRoom: HotelRoom) => {
    // plan categories are sorted by minimum price in ascending order first (in BFF)
    // then if there are multiple plan categories with same minimum price, sort by simplest to most complicated meal plan
    // no meal -> breakfast -> lunch -> dinner -> breakfast&lunch -> breakfast&dinner -> lunch&dinner -> breakfast&lunch&dinner
    const sortedMealPlansWithoutDuplicates =
      hotelRoom?.mealPlans &&
      [...(hotelRoom?.mealPlans as MealPlan[])]?.sort((a, b) => {
        const getScore = (meal: MealPlan['meal']) => {
          if (meal?.breakfast && !meal?.lunch && !meal?.dinner) return 1;
          if (!meal?.breakfast && meal?.lunch && !meal?.dinner) return 2;
          if (!meal?.breakfast && !meal?.lunch && meal?.dinner) return 3;
          if (meal?.breakfast && meal?.lunch && !meal?.dinner) return 4;
          if (meal?.breakfast && !meal?.lunch && meal?.dinner) return 5;
          if (!meal?.breakfast && meal?.lunch && meal?.dinner) return 6;
          if (meal?.breakfast && meal?.lunch && meal?.dinner) return 7;

          return 0;
        };

        if (a.totalPrice !== b.totalPrice) return 1;

        return getScore(a.meal) < getScore(b.meal) ? -1 : 1;
      });

    const categorizedMealPlans = sortedMealPlansWithoutDuplicates?.reduce(
      (accumulator: Record<string, MealPlan[]>, current) => {
        const category = getMealPlanCategoryText(current.meal) as string;
        return {
          ...accumulator,
          [category]: [
            ...((Object.keys(accumulator).includes(category)
              ? accumulator[category]
              : []) as MealPlan[]),
            current,
          ],
        };
      },
      {}
    ) as Record<string, MealPlan[]>;

    const renderPrice = (
      { totalPrice, discountedTotalPrice, memberDiscountedTotalPrice }: MealPlan,
      textSize = 'medium',
      hasTilde = true
    ) => {
      if (
        (!isLoggedIn && totalPrice && discountedTotalPrice && totalPrice > discountedTotalPrice) ||
        (isLoggedIn &&
          totalPrice &&
          memberDiscountedTotalPrice &&
          totalPrice > memberDiscountedTotalPrice)
      ) {
        return (
          <div className="flex shrink-0 items-start justify-end gap-2">
            <div className="flex flex-col items-end">
              <p
                className={classNames('font-bold whitespace-nowrap', {
                  'text-lg lg:text-2xl': textSize === 'medium',
                  'text-sm lg:text-2xl': textSize === 'small',
                })}
              >
                {totalPrice ? (
                  <>
                    {displayInYen(
                      isLoggedIn ? memberDiscountedTotalPrice : discountedTotalPrice,
                      router.locale
                    )}
                    {hasTilde ? ' ~' : ''}
                  </>
                ) : (
                  tHotel('availability.cannot_show_price')
                )}
              </p>
              <p className="whitespace-nowrap text-xs text-red-800 line-through lg:text-sm">
                {totalPrice ? (
                  <>
                    {displayInYen(totalPrice, router.locale)}
                    {hasTilde ? ' ~' : ''}
                  </>
                ) : (
                  tHotel('availability.cannot_show_price')
                )}
              </p>
            </div>
          </div>
        );
      }

      return (
        <p
          className={classNames('font-bold whitespace-nowrap', {
            'text-lg lg:text-2xl': textSize === 'medium',
            'text-sm lg:text-2xl': textSize === 'small',
          })}
        >
          {totalPrice ? (
            <>
              {displayInYen(totalPrice, router.locale)}
              {hasTilde ? ' ~' : ''}
            </>
          ) : (
            tHotel('availability.cannot_show_price')
          )}
        </p>
      );
    };

    if (planListingFeature) {
      const flatCategorizedMealPlans = Object.entries(categorizedMealPlans)
        .map(([category, mealPlans]) =>
          mealPlans.map((mealPlan) => ({
            category,
            mealPlan,
          }))
        )
        .flat();

      return (
        <div className="flex flex-col">
          {flatCategorizedMealPlans.map((categorizedMealPlan, index) => (
            <div
              key={categorizedMealPlan.category}
              className={classNames({
                'border-t border-gray-200 border-dotted': !!index,
                'lg:pt-4': !!index,
                'lg:pb-4': index !== flatCategorizedMealPlans.length - 1,
              })}
            >
              <MealPlanCard
                categorizedMealPlan={categorizedMealPlan}
                renderPrice={renderPrice}
                getLinkUrl={getLinkUrl}
                hotelRoom={hotelRoom}
                setRoomId={setRoomId}
              />
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-0 md:gap-4">
        {Object.entries(categorizedMealPlans).map(([category, mealPlans], index) => (
          <MealPlanAccordion
            key={category}
            isOpen={index === 0}
            category={category}
            mealPlans={mealPlans}
            renderPrice={renderPrice}
            getLinkUrl={getLinkUrl}
            hotelRoom={hotelRoom}
            setRoomId={setRoomId}
          />
        ))}
      </div>
    );
  };

  const renderRoomCards = () => {
    // loading
    if (getByIdLoading)
      return (
        <div className="flex h-[300px] w-full items-center justify-center">
          <Image src="/Loading.gif" alt="loading" width={200} height={200} />
        </div>
      );

    if (getByIdFailed) {
      reportMessage(
        `Error message displayed due to Server side error in Hotel Details Page Availability Section for ${hotelId}, checkInDate ${checkInAndOutDates.checkInDate}, checkOutDate ${checkInAndOutDates.checkOutDate}, roomCount ${roomAndGuestCount.roomCount}, guestCount ${guestCountString}`,
        'error'
      );
      return (
        <div className="flex h-[300px] flex-col items-center justify-center">
          <p className="pb-3 text-lg font-bold">{tHotel('availability.error_title')}</p>
          <p className="text-sm">{tHotel('availability.error_problem')}</p>
        </div>
      );
    }

    // No plans matched
    if (hotel && !hotel.hotelRooms?.length)
      return (
        <div className="flex h-[300px] flex-col items-center justify-center">
          <p className="pb-3 text-lg font-bold">{tHotel('availability.error_title')}</p>
          <p className="text-sm">{tHotel('availability.error_no_room')}</p>
        </div>
      );

    return (
      <div className="flex flex-col gap-6 pt-6">
        {hotel?.hotelRooms.map((room, i) => (
          <>
            {i % 4 === 0 && (
              <AuthenticationSection
                hasMemberDiscount={memberDiscountPercentage > discountPercentage}
                memberDiscountPercentage={memberDiscountPercentage}
              />
            )}
            <RoomCard
              key={room.name}
              hotelRoom={room}
              campaignTags={isLoggedIn ? room?.memberCampaignTags : room?.campaignTags}
              roomDetails
            >
              {renderPlanCategories(room)}
            </RoomCard>
          </>
        ))}
      </div>
    );
  };

  const linkToConfigurePage = getLinkUrl(
    roomId as number,
    mealPlanConfigModal?.targetSelectedMealPlanId?.toString() || ''
  );

  return (
    <>
      <h4 id="availability" className="pb-6 text-xl font-bold">
        {tHotel('availability.title')}
      </h4>
      <div className="flex w-full flex-col flex-wrap gap-2 rounded-md bg-white p-2.5 shadow-md lg:flex-row lg:gap-2.5">
        <div className="w-full flex-1">
          <DatePicker
            checkInAndOutDates={checkInAndOutDates}
            setCheckInAndOutDates={setCheckInAndOutDates}
          />
        </div>
        <div className="h-12 w-full flex-1 lg:h-14">
          <GuestCounter
            roomAndGuestCount={roomAndGuestCount}
            setRoomAndGuestCount={setRoomAndGuestCount}
          />
        </div>
        <div className="h-12 shrink-0 lg:h-14">
          <Button color="red" className="size-full" onClick={events.onClickSearch}>
            {tCommon('button.search_rooms')}
          </Button>
        </div>
      </div>

      {renderRoomCards()}

      {mealPlanConfigModal && (
        <PlanDetailModal
          mealPlanConfigModal={mealPlanConfigModal}
          actionButton={
            <Button
              size="large"
              color="white"
              textColor="gray"
              borderColor="gray"
              className="h-10 w-full px-0 sm:w-60"
              to={linkToConfigurePage}
              fontWeight="medium"
              border
            >
              <BsFillCheckCircleFill className="mr-2 text-gray-300" />
              <p className="text-gray-950">{tCommon('common:button.select')}</p>
            </Button>
          }
        />
      )}
    </>
  );
};

export default Availability;
